import type { JSX } from 'react';
import { CmsHtml } from '@packages/modules/src/CmsHtml/CmsHtml.client';
import { classes } from '../../../utils/classes';
import { type CmsData, ComponentTypes } from '../../../../interfaces';
import { ModalByTextlink } from '../../ModalByTextlink/ModalByTextlink';
import type { ModalByTextlinkType } from '../../ModalByTextlink/types';
import type { FooterBlockType, FooterBlockTypeGeneral } from '../types';

type SocketProps = {
  data: CmsData;
};

// Type guard to check if the object is a ModalByTextlink component type
const isModalByTextlinkType = (
  item: FooterBlockType,
): item is FooterBlockTypeGeneral & ModalByTextlinkType =>
  (item.type as ComponentTypes) === ComponentTypes.TextlinkModal &&
  (item as ModalByTextlinkType).field_type === 'textlink_modal';

// Type guard to check if the object is an Html component type
const isHtmlType = (item: unknown): item is { type: ComponentTypes.Html; [key: string]: unknown } =>
  typeof item === 'object' &&
  item !== null &&
  'type' in item &&
  (item as { type: unknown }).type === ComponentTypes.Html;

export const AreaSocket = ({ data }: SocketProps): JSX.Element => {
  const comp = (data.paragraph as FooterBlockType[])
    ?.filter((item) => item.parent_field_name === 'field_socket')
    ?.map((item, index) => {
      const keyModalByTextlink = `socket-${ComponentTypes.TextlinkModal}-${index}`;
      const keyHtml = `socket-${ComponentTypes.Html}-${index}`;
      if (isModalByTextlinkType(item)) {
        return <ModalByTextlink key={keyModalByTextlink} data={item} />;
      }

      if (isHtmlType(item)) {
        return <CmsHtml key={keyHtml} data={item} />;
      }

      // eslint-disable-next-line react/no-array-index-key
      return <div key={`socket-${index}`} />;
    });

  if (data && comp.length > 0) {
    return (
      <div
        className={classes(
          'text-color-footer-contrasttext text-size-body-sm mx-auto max-w-screen-xl px-2! pt-3 pb-[4.5rem] md:px-0!',
          '[&_a]:text-color-footer-contrasttext! [&_a]:decoration-color-text [&_a]:underline',
          '[&_dialog_a]:text-color-text! [&_dialog_button]:text-color-text!', // edge case: if the link/button opens a modal, the modal has the light theme again.
          '[&_p]:m-0 [&_p]:p-0 [&_p+p]:pt-1.5! md:[&_p+p]:pt-1!',
        )}
      >
        {comp}
      </div>
    );
  }

  return <div />;
};
